import { AnyAction } from 'redux';
import { FormikErrors } from 'formik';

import {
    SUPPLIER_CONDITIONS_DELIVERY_TERMS_RECEIVED,
    SUPPLIER_CONDITIONS_RECEIVED,
    SUPPLIER_CONDITIONS_UPDATE,
    SUPPLIER_CONDITIONS_UPDATE_ARCHIVE_CONDITION,
} from './supplier-conditions-actions';
import { SupplierConditions } from '../models/supplier-conditions';
import { SupplierConditionsFormValues } from './../models/supplier-conditions-form-values';
import { Term } from '../models/term';

export interface SupplierConditionsReducerState {
    detailConditions: SupplierConditionsFormValues | null;
    deliveryTerms: Term[];
    isValid: boolean;
    isDirty: boolean;
    /**
     * The conditions as the server last handed them over (#2159).
     * `detailConditions` cannot serve: leaving the tab parks its input there.
     */
    baseline?: SupplierConditionsFormValues | null;
    errors: FormikErrors<SupplierConditions>;
}

export const initialSupplierConditionsReducerState: SupplierConditionsReducerState = {
    detailConditions: null,
    deliveryTerms: [],
    isValid: true,
    isDirty: false,
    errors: {},
};

export const supplierConditionsReducer = (
    state: SupplierConditionsReducerState = initialSupplierConditionsReducerState,
    action: AnyAction
): SupplierConditionsReducerState => {
    switch (action.type) {
        case SUPPLIER_CONDITIONS_RECEIVED: {
            const detailConditions: SupplierConditionsFormValues = {
                ...action['conditions'],
                deliveryTermId: action['conditions'].deliveryTerm?.id,
            };

            return {
                ...state,
                detailConditions,
                // The same values, kept apart from the ones the tab edits.
                baseline: detailConditions,
                isDirty: false,
            };
        }

        case SUPPLIER_CONDITIONS_DELIVERY_TERMS_RECEIVED: {
            return {
                ...state,
                deliveryTerms: action['terms'],
            };
        }

        case SUPPLIER_CONDITIONS_UPDATE: {
            const { formData, isDirty, isValid, errors } = action;

            const nextConditions = {
                ...state.detailConditions,
                ...formData,
                freeShippingThreshold: formData.freeShippingThreshold ?? null,
                bonusClassification: formData.bonusClassification,
                bonusClassificationTo: formData.bonusClassification ? formData.bonusClassificationTo : null,
                bonusClassificationFrom: formData.bonusClassification ? formData.bonusClassificationFrom : null,
            };

            return {
                ...state,
                detailConditions: nextConditions,
                isDirty: state.isDirty || isDirty,
                isValid,
                errors,
            };
        }

        case SUPPLIER_CONDITIONS_UPDATE_ARCHIVE_CONDITION: {
            const { id, isDirty } = action;

            const nextConditions = state.detailConditions?.conditions.map(condition =>
                condition.id === id ? { ...condition, isArchived: !condition.isArchived } : condition
            );

            if (!nextConditions || !state.detailConditions) {
                return state;
            }

            return {
                ...state,
                detailConditions: {
                    ...state.detailConditions,
                    conditions: nextConditions,
                },
                isDirty: state.isDirty || isDirty,
            };
        }

        default: {
            return state;
        }
    }
};
